/**
 * My harvests (web) — the independent (G1) farmer's own harvests.
 *
 * Web port of apps/mobile/app/my-harvests/index.tsx. Lists the signed-in
 * farmer's own harvests (a dedicated `individual_harvests` table, owner-scoped
 * server-side from the JWT), a status filter row, a tap-through to detail, and
 * a "Record a harvest" dialog that POSTs /harvests (the server derives
 * owner_user_id from the caller).
 *
 * READ-ONLY in a coop: when the farmer belongs to a cooperative
 * (farm-profile.cooperativeId !== null) their individual harvests freeze to a
 * read-only log — no create. We fail OPEN: if the farm-profile read errors or
 * 404s (e.g. a non-farmer), we treat the user as NOT in a coop.
 *
 * Unlike mobile, web is ONLINE-ONLY — there is no SQLite cache, no sync-engine
 * outbox, and no offline banners. Plain react-query online flows.
 *
 * Endpoints: GET /harvests, POST /harvests, GET /me/farm-profile.
 * Uses the generated orval clients src/api/generated/harvests + .../me (TD-070).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Text,
  TextField,
  type ChipColor,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import { meControllerGetFarmProfile } from '../api/generated/me/me';
import {
  harvestsControllerCreate,
  harvestsControllerList,
} from '../api/generated/harvests/harvests';
import { HarvestDtoStatus, type HarvestDto } from '../api/generated/models';
import { groupDigits } from '../lib/format';

// 'listed' is a real runtime status (locked → listed on marketplace publish),
// but orval hasn't regen'd the HarvestDtoStatus union to include it yet (regen
// deferred until after deploy). Widen with the string literal so the filter row
// + h.status comparison compile until the union catches up.
type StatusFilter = 'all' | HarvestDtoStatus | 'listed';

// Mirrors the mobile filter row — surfaces the happy-path statuses. 'cancelled'
// is reachable but rare; the 'all' filter still shows cancelled rows.
const STATUS_FILTERS: StatusFilter[] = ['all', 'open', 'locked', 'listed', 'sold', 'settled'];

/**
 * Map harvest lifecycle → kit `Chip` colour (assist chip used as a status
 * badge; the web kit has no separate `Tag`). open → neutral (outlined), locked
 * → warning (mango), listed/sold/settled → success (leaf), cancelled → neutral.
 */
const STATUS_CHIP: Record<HarvestDtoStatus, ChipColor> = {
  open: 'neutral',
  locked: 'warning',
  listed: 'success',
  sold: 'success',
  settled: 'success',
  cancelled: 'neutral',
};

/**
 * Chip colour for a harvest status, defensive against the deferred-regen gap:
 * `'listed'` exists at runtime but isn't a key in STATUS_CHIP yet, so we
 * string-key the lookup → 'success' (leaf) for 'listed', the mapped colour for
 * known statuses, 'neutral' as a fallback for anything unexpected.
 */
function chipColorFor(status: string): ChipColor {
  if (status === 'listed') return 'success';
  return STATUS_CHIP[status as HarvestDtoStatus] ?? 'neutral';
}

/** Short harvest identifier for human display — first 8 chars of UUID. */
function shortId(id: string): string {
  return id.slice(0, 8);
}

function HarvestCard({ harvest, t }: { harvest: HarvestDto; t: TFunction }) {
  return (
    <Link
      to="/my-harvests/$id"
      params={{ id: harvest.id }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Card sx={{ cursor: 'pointer' }}>
        <CardContent>
          <Stack spacing={1.5}>
            {/* Header — harvest short-id + status chip */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Text variant="title-small" color="text.primary">
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  {t('myHarvests.card', { id: shortId(harvest.id) })}
                </Box>
              </Text>
              <Chip
                kind="assist"
                color={chipColorFor(harvest.status)}
                label={t(`myHarvests.status.${harvest.status}`)}
              />
            </Box>

            {/* totalKg arrives as a string — display verbatim. */}
            <Text variant="headline-small">
              {t('myHarvests.totalKg', { qty: groupDigits(harvest.totalKg) })}
            </Text>

            {harvest.crop ? (
              <Text variant="body-medium" color="text.secondary">
                {t('myHarvests.cropLabel', { crop: harvest.crop })}
              </Text>
            ) : null}

            <Text variant="body-medium" color="text.secondary">
              {t('myHarvests.contributionCount', { count: harvest.contributionCount })}
            </Text>
          </Stack>
        </CardContent>
      </Card>
    </Link>
  );
}

interface CreateHarvestDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  t: TFunction;
}

function CreateHarvestDialog({ open, onClose, onCreated, t }: CreateHarvestDialogProps) {
  const [note, setNote] = useState('');
  const [crop, setCrop] = useState('');

  const createMutation = useMutation({
    mutationFn: (vars: { note?: string; crop?: string }) =>
      harvestsControllerCreate({
        ...(vars.note ? { note: vars.note } : {}),
        ...(vars.crop ? { crop: vars.crop } : {}),
      }),
    onSuccess: () => {
      setNote('');
      setCrop('');
      onCreated();
    },
  });

  const close = () => {
    if (createMutation.isPending) return;
    setNote('');
    setCrop('');
    createMutation.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('myHarvests.new.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('myHarvests.new.body')}</DialogContentText>
        <TextField
          label={t('myHarvests.new.cropLabel')}
          value={crop}
          onChange={(e) => setCrop(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 120 } }}
          disabled={createMutation.isPending}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label={t('myHarvests.new.noteLabel')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={1}
          maxRows={4}
          slotProps={{ htmlInput: { maxLength: 500 } }}
          disabled={createMutation.isPending}
          fullWidth
        />
        {createMutation.isError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('myHarvests.new.error', {
              error:
                createMutation.error instanceof FseApiError
                  ? createMutation.error.message
                  : String(createMutation.error),
            })}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={close} disabled={createMutation.isPending}>
          {t('myHarvests.new.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            createMutation.mutate({
              note: note.trim() || undefined,
              crop: crop.trim() || undefined,
            })
          }
          disabled={createMutation.isPending}
          startIcon={createMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {t('myHarvests.new.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function MyHarvestsScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sub = readAccessTokenClaims()?.sub;

  const [filter, setFilter] = useState<StatusFilter>('all');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Read-only when in a coop. Fail OPEN: on error/404 treat as NOT in a coop.
  // Shared cache key with the home screen.
  const farmProfileQuery = useQuery({
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    enabled: !!sub,
    retry: false,
  });
  const isInCoop = farmProfileQuery.data?.cooperativeId != null;

  const harvestsQuery = useQuery({
    queryKey: ['my-harvests'],
    queryFn: ({ signal }) => harvestsControllerList(undefined, signal),
    enabled: !!sub,
  });

  const rows = harvestsQuery.data?.data ?? [];
  const total = harvestsQuery.data?.pagination.total;

  const visibleHarvests = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((h) => h.status === filter);
  }, [rows, filter]);

  function onCreated() {
    queryClient.invalidateQueries({ queryKey: ['my-harvests'] });
    setDialogOpen(false);
  }

  // Could not derive the caller's identity from the token — surface as an error
  // rather than firing a query we know will be rejected.
  if (!sub) {
    return (
      <Stack spacing={3}>
        <Header t={t} total={undefined} />
        <Alert severity="error">{t('myHarvests.noIdentity')}</Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Header t={t} total={total} />
        {!isInCoop ? (
          <Button variant="contained" onClick={() => setDialogOpen(true)}>
            {t('myHarvests.new')}
          </Button>
        ) : null}
      </Box>

      {isInCoop ? <Alert severity="info">{t('myHarvests.coopReadOnly')}</Alert> : null}

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {STATUS_FILTERS.map((f) => (
          <Chip
            key={f}
            kind="filter"
            label={t(`myHarvests.filter.${f}`)}
            selected={filter === f}
            onClick={() => setFilter(f)}
          />
        ))}
      </Stack>

      {harvestsQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : harvestsQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => harvestsQuery.refetch()}>
              {t('myHarvests.retry')}
            </Button>
          }
        >
          {t('myHarvests.loadError', {
            error:
              harvestsQuery.error instanceof FseApiError
                ? harvestsQuery.error.message
                : String(harvestsQuery.error),
          })}
        </Alert>
      ) : visibleHarvests.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
              <Text variant="title-medium" as="h3">
                {t(isInCoop ? 'myHarvests.empty.coopTitle' : 'myHarvests.empty.title')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t(isInCoop ? 'myHarvests.empty.coopBody' : 'myHarvests.empty.body')}
              </Text>
              {!isInCoop ? (
                <Button variant="contained" onClick={() => setDialogOpen(true)}>
                  {t('myHarvests.new')}
                </Button>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {visibleHarvests.map((h) => (
            <HarvestCard key={h.id} harvest={h} t={t} />
          ))}
        </Stack>
      )}

      <CreateHarvestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={onCreated}
        t={t}
      />
    </Stack>
  );
}

function Header({ t, total }: { t: TFunction; total: number | undefined }) {
  return (
    <Box>
      <Text variant="headline-small" as="h2">
        {t('myHarvests.title')}
      </Text>
      <Box sx={{ mt: 0.5 }}>
        <Text variant="body-medium" color="text.secondary">
          {typeof total === 'number'
            ? t('myHarvests.subtitleWithCount', { total })
            : t('myHarvests.subtitle')}
        </Text>
      </Box>
    </Box>
  );
}
